import React, { useState, useRef, useEffect } from 'react';
import { machineSchedulingOptionSuffix } from '../utils/machineScheduling';

const SearchableDropdown = ({
  options = [],
  value = '',
  onChange,
  placeholder = 'Search...',
  emptyMessage = 'No options found',
  maxHeight = '200px',
  disabled = false,
  className = '',
  name = '',
  label = '',
  displayKey = 'name',
  valueKey = 'id',
  searchKeys = ['name'],
  showCustomer = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Filter options based on search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter(option => {
        return searchKeys.some(key => {
          const fieldValue = option[key];
          if (fieldValue) {
            return fieldValue.toLowerCase().includes(searchTerm.toLowerCase());
          }
          return false;
        });
      });
      setFilteredOptions(filtered);
    }
  }, [searchTerm, options, searchKeys]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchTerm('');
      }
    }
  };

  const handleOptionClick = (option) => {
    onChange({
      target: {
        name: name,
        value: option[valueKey]
      }
    });
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = () => {
    onChange({
      target: {
        name: name,
        value: ''
      }
    });
    setIsOpen(false);
    setSearchTerm('');
  };

  const getDisplayText = (option) => {
    let text;
    if (showCustomer && option.customer) {
      text = `${option[displayKey]} - ${option.customer.companyName}`;
    } else if (displayKey === 'name' && option.model) {
      text = `${option[displayKey]} - ${option.model}`;
    } else {
      text = option[displayKey];
    }
    if (option && Object.prototype.hasOwnProperty.call(option, 'schedulingBlocked')) {
      return text + machineSchedulingOptionSuffix(option);
    }
    return text;
  };

  const getSelectedOption = () => {
    return options.find(option => option[valueKey] == value);
  };

  const selectedOption = getSelectedOption();

  const dropdownStyles = {
    container: {
      position: 'relative',
      width: '100%'
    },
    toggle: {
      position: 'relative',
      backgroundColor: '#fff',
      border: '1px solid #ced4da',
      borderRadius: '0.375rem',
      padding: '0.375rem 0.75rem',
      fontSize: '1rem',
      lineHeight: '1.5',
      color: '#212529',
      backgroundClip: 'padding-box',
      transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      cursor: disabled ? 'not-allowed' : 'pointer',
      width: '100%',
      textAlign: 'left',
      appearance: 'none', // Remove default browser styling
      WebkitAppearance: 'none', // Remove default browser styling for webkit
      MozAppearance: 'none', // Remove default browser styling for firefox
      backgroundImage: 'none', // Remove any background images (including arrows)
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'right center',
      backgroundSize: 'auto'
    },
    toggleHover: {
      borderColor: '#86b7fe'
    },
    toggleDisabled: {
      backgroundColor: '#e9ecef',
      opacity: 1
    },
    selectedText: {
      flex: 1,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    actions: {
      display: 'flex',
      alignItems: 'center',
      marginLeft: '8px'
    },
    clearBtn: {
      background: 'none',
      border: 'none',
      padding: '0 4px',
      marginRight: '4px',
      color: '#6c757d',
      cursor: 'pointer',
      fontSize: '18px',
      lineHeight: 1
    },
    arrow: {
      fontSize: '12px',
      color: '#6c757d',
      marginLeft: '4px'
    },
    menu: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      zIndex: 1000,
      backgroundColor: '#fff',
      border: '1px solid #ced4da',
      borderRadius: '0.375rem',
      boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)',
      overflow: 'hidden',
      minWidth: '300px', // Make dropdown wider
      width: 'max-content' // Allow it to expand based on content
    },
    searchContainer: {
      padding: '12px', // Increased padding
      borderBottom: '1px solid #e9ecef',
      backgroundColor: '#f8f9fa'
    },
    searchInput: {
      width: '100%',
      padding: '10px 12px', // Increased padding
      border: '1px solid #ced4da',
      borderRadius: '4px',
      fontSize: '14px',
      outline: 'none',
      backgroundColor: '#fff'
    },
    optionsContainer: {
      maxHeight: '250px', // Fixed height for scrolling
      overflowY: 'auto',
      overflowX: 'hidden'
    },
    option: {
      padding: '12px 16px', // Increased padding
      cursor: 'pointer',
      fontSize: '14px',
      borderBottom: '1px solid #f8f9fa',
      backgroundColor: 'transparent',
      transition: 'background-color 0.15s ease-in-out',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    optionSelected: {
      backgroundColor: '#e3f2fd',
      color: '#1976d2',
      fontWeight: 500
    },
    noOptions: {
      padding: '16px', // Increased padding
      textAlign: 'center',
      color: '#6c757d',
      fontSize: '14px'
    }
  };

  return (
    <>
      <style>
        {`
          .custom-dropdown-toggle {
            appearance: none !important;
            -webkit-appearance: none !important;
            -moz-appearance: none !important;
            background-image: none !important;
            background: #fff !important;
            background-size: auto !important;
            background-repeat: no-repeat !important;
            background-position: right center !important;
          }
          .custom-dropdown-toggle::after {
            display: none !important;
            content: none !important;
          }
          .custom-dropdown-toggle::before {
            display: none !important;
            content: none !important;
          }
          .custom-dropdown-toggle::-ms-expand {
            display: none !important;
          }
          .custom-dropdown-toggle::-webkit-appearance {
            display: none !important;
          }
          .custom-dropdown-toggle::-moz-appearance {
            display: none !important;
          }
          /* Override Bootstrap form-select styles */
          .custom-dropdown-toggle.form-select {
            background-image: none !important;
            background-size: auto !important;
            background-repeat: no-repeat !important;
            background-position: right center !important;
          }
        `}
      </style>
      <div className={`searchable-dropdown ${className}`} ref={dropdownRef} style={dropdownStyles.container}>
        {label && (
          <label className="form-label">{label}</label>
        )}
      
      <div style={dropdownStyles.container}>
        <button
          type="button"
          className={`custom-dropdown-toggle ${disabled ? 'disabled' : ''}`}
          onClick={handleToggle}
          disabled={disabled}
          style={{
            ...dropdownStyles.toggle,
            ...(disabled ? dropdownStyles.toggleDisabled : {})
          }}
          onMouseEnter={(e) => {
            if (!disabled) {
              e.target.style.borderColor = '#86b7fe';
            }
          }}
          onMouseLeave={(e) => {
            if (!disabled) {
              e.target.style.borderColor = '#ced4da';
            }
          }}
        >
          <span style={dropdownStyles.selectedText}>
            {selectedOption ? getDisplayText(selectedOption) : placeholder}
          </span>
          <div style={dropdownStyles.actions}>
            {selectedOption && !disabled && (
              <button
                type="button"
                style={dropdownStyles.clearBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  handleClear();
                }}
                onMouseEnter={(e) => {
                  e.target.style.color = '#dc3545';
                }}
                onMouseLeave={(e) => {
                  e.target.style.color = '#6c757d';
                }}
              >
                ×
              </button>
            )}
            <span style={dropdownStyles.arrow}>
              {isOpen ? '▲' : '▼'}
            </span>
          </div>
        </button>

        {isOpen && (
          <div style={dropdownStyles.menu}>
            <div style={dropdownStyles.searchContainer}>
              <input
                ref={searchInputRef}
                type="text"
                className="search-input"
                placeholder={placeholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={dropdownStyles.searchInput}
                onFocus={(e) => {
                  e.target.style.borderColor = '#86b7fe';
                  e.target.style.boxShadow = '0 0 0 0.25rem rgba(13, 110, 253, 0.25)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#ced4da';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
            
            <div 
              style={dropdownStyles.optionsContainer}
              onScroll={(e) => {
                // Ensure smooth scrolling
                e.target.style.scrollBehavior = 'smooth';
              }}
            >
              {filteredOptions.length === 0 ? (
                <div style={dropdownStyles.noOptions}>
                  {emptyMessage}
                </div>
              ) : (
                filteredOptions.map((option, index) => (
                  <div
                    key={option[valueKey] || index}
                    className={`dropdown-option ${value == option[valueKey] ? 'selected' : ''}`}
                    onClick={() => handleOptionClick(option)}
                    style={{
                      ...dropdownStyles.option,
                      ...(value == option[valueKey] ? dropdownStyles.optionSelected : {})
                    }}
                    onMouseEnter={(e) => {
                      if (value != option[valueKey]) {
                        e.target.style.backgroundColor = '#f8f9fa';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (value != option[valueKey]) {
                        e.target.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    {getDisplayText(option)}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default SearchableDropdown;